import React, { useEffect, useRef, useState } from 'react';
import { ICapabilityProps } from './interface';
import { isInApp, isInMobile } from '@umu/umu-ua';
import { useInViewport } from 'ahooks';
import merge from 'lodash/merge';
import { onFormatterLabel } from './dataFlow';
import { If } from 'common/src/components/if';
import { useBizcharts } from '@umu/umu-react-hooks';
import Loading from 'common/src/components/loading/loading';

const isApp = isInApp() || isInMobile();

const axisDefaultGrid = {
    line: {
        type: 'line',
        style: {
            stroke: '#D6DDE3', // 网格线的颜色
            lineWidth: 1, // 网格线的宽度复制代码
        },
    },
    alternateColor: ['rgb(250,251,255,0.6)', 'rgb(255,255,255,0.6)'],
};

const scaleDefault = {
    value: {
        min: 0,
        max: 100,
        tick: 5,
        ticks: [0, 20, 40, 60, 80, 100],
        formatter: (val: number) => {
            return `${val}`;
        },
    },
};

const tooltipDefault = {
    shared: true,
};

const selectLabelDefaultConfig = {
    color: '#1e6ee6',
    defaultColor: '#222',
    isMultiSelect: false,
};

interface IProps extends ICapabilityProps {
    height?: number;
    isSwitchOnLabelSelect?: boolean;
    color?: string | [string, (xVal: string) => string];
    // https://bizcharts.taobao.com/product/BizCharts4/category/62/page/79#grid
    axisGrid?: any;
    // https://bizcharts.taobao.com/product/BizCharts4/category/62/page/77#scale
    scale?: any;
    // https://bizcharts.taobao.com/product/BizCharts4/category/62/page/80#shared
    tooltip?: any;
    selectLabelConfig?: {
        color?: string; // 选中颜色
        defaultColor?: string; // 默认颜色
        isMultiSelect?: boolean; // 是否多选
    };
    customTooltip?: null | ((xVal: string, info: any) => React.ReactNode);
    onLabelSelect?: (ids: string[]) => void;
    linePadding?: number[];
    loading?: boolean;
}
function LineChart(props: IProps) {
    const {
        data = [],
        height = isApp ? 260 : 360,
        isSwitchOnLabelSelect = false,
        axisGrid = axisDefaultGrid,
        selectLabelConfig,
        color,
        customTooltip,
        tooltip = tooltipDefault,
        scale = scaleDefault,
        preCls,
        onLabelSelect,
        formatterLabel,
        loading,
        chartProps = {},
    } = props;
    const radarRef = useRef(null);
    const labelSelectRef = useRef<string[]>([]);
    const [labelSelect, setLabelSelect] = useState<string[]>([]);
    const [isShow, setIsShow] = useState(false);
    const [inViewport] = useInViewport(radarRef);
    useEffect(() => {
        if (inViewport && !isShow) {
            setIsShow(true);
        }
    }, [inViewport]);
    const labelConfig = merge(selectLabelDefaultConfig, selectLabelConfig);
    const defaultLabelConfig = {
        fontSize: 14,
        lineHeight: 20,
        fill: labelConfig.defaultColor,
    };
    const onChangeSelectLabel = (info: string[]) => {
        setLabelSelect(info);
        onLabelSelect?.(info);
    };
    const onAxisLabelClick = (ev: any) => {
        const id = ev.target?.cfg?.delegateObject?.item?.id;
        let newLabelList = labelSelectRef.current;
        const isHas = newLabelList.includes(id);
        const isMulti = labelConfig.isMultiSelect;
        if (isHas) {
            newLabelList = isMulti ? newLabelList.filter((item) => item !== id) : [];
        } else {
            newLabelList = isMulti ? [...newLabelList, id] : [id];
        }
        labelSelectRef.current = newLabelList;
        onChangeSelectLabel(newLabelList);
    };
    const onGetG2Instance = (chart: any) => {
        if (isSwitchOnLabelSelect) {
            // 开启标签点击事件
            chart.on('axis-label:click', onAxisLabelClick);
        }
    };

    const BizChartsModules = useBizcharts();

    if (!BizChartsModules) {
        return <Loading />;
    }

    const { Chart, Point, Line, Axis, Tooltip, Legend } = BizChartsModules;

    return (
        <div className={preCls} ref={radarRef}>
            <If condition={!isShow || !!loading}>
                <Loading />
            </If>
            <If condition={isShow && !loading}>
                <Chart
                    height={height}
                    data={data}
                    padding={props.linePadding || [20, 40, 20, 40]}
                    autoFit
                    scale={scale}
                    onGetG2Instance={onGetG2Instance}
                    {...chartProps}
                >
                    <Legend />
                    <Tooltip {...tooltip}>{customTooltip}</Tooltip>
                    <Line
                        position="label*value"
                        size={2}
                        color={color}
                        style={{
                            lineWidth: 1,
                        }}
                    />
                    <Point
                        label={['value', { style: { fill: '#222', fontSize: 14 } }]}
                        size={5}
                        position="label*value"
                        color="#fff"
                        shape="hollow-circle"
                    />
                    <Point size={3} position="label*value" color={color} shape="circle" />
                    <Axis
                        name="value"
                        grid={axisGrid}
                        tickLine={false}
                        line={false}
                        label={{ style: defaultLabelConfig }}
                    />
                    <Axis
                        name="label"
                        line={false}
                        label={{
                            autoHide: false,
                            autoEllipsis: true,
                            style: isSwitchOnLabelSelect
                                ? (_val, index) => {
                                      const itemInfo = data[index];
                                      return {
                                          ...defaultLabelConfig,
                                          fill: labelSelect.includes(itemInfo?.label)
                                              ? labelConfig.color
                                              : labelConfig.defaultColor,
                                      };
                                  }
                                : defaultLabelConfig,
                            formatter(text) {
                                const label = formatterLabel ? formatterLabel(text) : text;
                                return onFormatterLabel(label);
                            },
                        }}
                    />
                </Chart>
            </If>
        </div>
    );
}
export { LineChart };
